import React, { FC }  from 'react';
import * as Yup       from "yup";
import { Cell, Grid } from "styled-css-grid";

import { CorvalProjectActivity, Uuid }                                                                                           from "@src/types";
import { formatOption }                                                                                                          from "@src/util";
import { FieldDescription, FieldWrapper, SelectField, TextField }                                                                from "@src/views/components/forms";
import { BulkUpdateFormControlContainer }                                                                                        from "@src/views/pages/projects/activities/bulk/BulkUpdateFormControlContainer";
import { ProjectActivityBulkUpdateShell, ProjectActivityBulkUpdateShellFormContainerProps, ProjectActivityBulkUpdateShellProps } from "@src/views/pages/projects/activities/bulk/ProjectActivityBulkUpdateShell";
import { ProjectActivityBulkUpdateMultiDialogProps }                                                                             from "@src/views/pages/projects/operations/ProjectActivityBulkUpdateMultiDialog";
import { FieldLabel }                                                                                                            from "@src/views/components/forms/formik/FieldLabel";
import { SemanticICONS }                                                                                                         from "semantic-ui-react";


const CorvalReversionValue = 'CORVAL',
      CorvalReversionLabel = 'Use Corval Calculated Costs',
      NoCostValue          = 'NONE',
      NoCostLabel          = 'No Cost',
      CustomCostValue      = 'CUSTOM',
      CustomCostLabel      = 'Use Specific Cost...';

type FormTypeAttributes = {
    costType: Uuid | null,
    costValue: number | null,
    attribute: keyof CorvalProjectActivity,
}

const formDefaultValues = {
    costType:  null,
    costValue: null,
    attribute: null,
}

const validationSchema = (
    Yup.object().shape( {
        costType:  Yup.string().nullable().required( 'Please choose an option' ),
        costValue: Yup.number().when( 'costType', {
            is:        ( costType ) => ( costType === CorvalReversionValue ),
            then:      Yup.number().nullable(),
            otherwise: Yup.number().min( 0, 'Must be zero or more.' ).required( 'Must be zero or more.' )
        } ),
    } )
)

const mutationAttributeFormatter: ProjectActivityBulkUpdateShellProps<FormTypeAttributes>['prepareFormValuesForMutation'] = ( values ) => {

    const useCorvalRecommended = ( values.costType === CorvalReversionValue ),
          useZeroCost          = ( values.costType === NoCostValue ) || ( values.costValue === 0 )

    let { costValue } = values;

    if( useCorvalRecommended ){
        costValue = null;
    }
    if( useZeroCost ){
        costValue = 0;
    }

    // Return the operational cost override, or null for Corval recommended.
    return { [ values.attribute ]: costValue }
}

interface ProjectActivityBulkUpdateCostAttributeProps extends Pick<ProjectActivityBulkUpdateShellProps<FormTypeAttributes>, 'supportModals' | 'supportToasts'>,
    Pick<ProjectActivityBulkUpdateMultiDialogProps, 'projectId' | 'projectVersionId' | 'projectActivityIds'> {
    formContainer: FC<ProjectActivityBulkUpdateShellFormContainerProps>,
    attributeName: keyof CorvalProjectActivity,
    icon: SemanticICONS,
    title: string,
    description: string,
    fieldLabel: string,
    fieldDescription: string,
}

const ProjectActivityBulkUpdateCostAttributeDefaultProps = {
    supportModals: false,
    supportToasts: true,
} satisfies Partial<ProjectActivityBulkUpdateCostAttributeProps>

function ProjectActivityBulkUpdateCostAttribute( _props: ProjectActivityBulkUpdateCostAttributeProps ){

    const props = { ...ProjectActivityBulkUpdateCostAttributeDefaultProps, ..._props };

    return (

        <ProjectActivityBulkUpdateShell<FormTypeAttributes>
            projectId={ props.projectId }
            projectVersionId={ props.projectVersionId }
            ids={ props.projectActivityIds }
            supportToasts={ props.supportToasts }
            supportModals={ props.supportModals }
            defaultFormValues={ { ...formDefaultValues, attribute: props.attributeName } }
            validationSchema={ validationSchema }
            formContainer={ props.formContainer }
            prepareFormValuesForMutation={ mutationAttributeFormatter }>

            {/* FORM CONTENT */ }
            { ( { formik, isProcessing, isLoading } ) => {

                const useCostInput = [ CustomCostValue ].includes( formik.values.costType );

                return (

                    <BulkUpdateFormControlContainer
                        icon={ props.icon }
                        title={ props.title }
                        description={ `${ props.description } Choosing "No Cost" will set the cost to zero. Choosing "${ CorvalReversionLabel }" will reset all activities to their Corval calculated costs.` }>

                        <FieldWrapper>

                            <FieldLabel>{ props.fieldLabel }</FieldLabel>
                            <FieldDescription>{ props.fieldDescription }</FieldDescription>

                            <Grid columns={ `50% 50%` }>

                                <Cell>

                                    <SelectField
                                        fluid
                                        disabled={ isLoading || isProcessing }
                                        name={ `costType` }
                                        placeholder={ 'Set Cost To...' }
                                        selectOnNavigation={ true }
                                        options={ [
                                            formatOption( NoCostValue, NoCostLabel ),
                                            formatOption( CorvalReversionValue, CorvalReversionLabel ),
                                            formatOption( CustomCostValue, CustomCostLabel ),
                                        ] }
                                        onChange={ ( value ) => {
                                            if( value === NoCostValue ){
                                                formik.setFieldValue( 'costValue', 0 );
                                            }
                                            if( [ CorvalReversionValue, CustomCostValue ].includes( value ) ){
                                                formik.setFieldValue( 'costValue', '' );
                                            }
                                        } }
                                    />

                                </Cell>
                                <Cell>

                                    { useCostInput && (
                                        <TextField
                                            type='number'
                                            min={ 0 }
                                            name={ `costValue` }
                                            icon={ 'dollar' }
                                            iconPosition={ 'left' }
                                            disabled={ isLoading || isProcessing }
                                        />
                                    ) }

                                </Cell>

                            </Grid>

                        </FieldWrapper>

                    </BulkUpdateFormControlContainer>

                )
            } }

        </ProjectActivityBulkUpdateShell>

    );
}

export {
    ProjectActivityBulkUpdateCostAttribute,
    ProjectActivityBulkUpdateCostAttributeDefaultProps,
    type ProjectActivityBulkUpdateCostAttributeProps,
};
